import * as vp from 'vopay';

import * as Yup from 'yup';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, FormikErrors, FormikTouched, useFormikContext } from 'formik';
import styles from '../AccountingSystemOpen.module.scss';
import useFetchFinancialInstitutionBranches from '#shared/components/AccountingSoftware/hooks/useFetchFinancialInstitutionBranches';
import {
  buildPaymentUpdateRows,
  PaymentUpdateRows,
  getPaymentMethodsNeededDropdown,
} from './buildPaymentUpdateRows.helper';
import { PaymentUpdate } from '../types';
import { ELinxConnect } from 'shared/eLinx';
import useElinxRows from '../Elinx/useElinxRows';
import ElinxInfoBanner from '../Elinx/ElinxInfoBanner';
import { getRequiredIcon, mergeElinxDefaults, isIQ11Enabled, isElinxActive, hasElinxPermission } from '../utils';
import { getAspRowExtras } from '#shared/components/AccountingSoftware/aspAccountingAdapter';
import { useAccountingSoftware } from '#shared/components/AccountingSoftware/AccountingSoftwareContext';
import {
  getDefaultBankingPaymentRails,
  getDefaultPaymentSelectionMethods,
  toElinxCountry,
} from '#shared/components/AccountingSoftware/elinxDefaults.helper';

import { Button } from '#shared/components/UI';

type BankingUpdateDialogueProps = {
  isVisible: boolean;
  type: 'Payable' | 'Receivable';
  updateType: 'Edit' | 'Add';
  defaultValues: PaymentUpdate;
  originalValue: vp.TemporaryBillsOrInvoicesRails | undefined;
  financialInstitutions: {
    [key: string]: vp.FinancialInstitution;
  };
  close: () => void;
  onSave: (savedValues: PaymentUpdate) => void;
};
type FC = FunctionComponent<BankingUpdateDialogueProps>;

type BankingUpdateMiniFormEffectsProps = {
  setCurrentValues: (values: PaymentUpdate) => void;
  paymentUpdateRows: ReturnType<typeof buildPaymentUpdateRows>;
  fetchFinancialInstitutionBranches: ReturnType<typeof useFetchFinancialInstitutionBranches>[1];
  getFinancialInstitutionIDByNumber: (
    financialInstitutionNumber: string,
  ) => vp.FinancialInstitution['FinancialInstitutionID'] | null;
};

function BankingUpdateMiniFormEffects({
  setCurrentValues,
  paymentUpdateRows,
  fetchFinancialInstitutionBranches,
  getFinancialInstitutionIDByNumber,
}: BankingUpdateMiniFormEffectsProps) {
  const { values, validateForm } = useFormikContext<PaymentUpdate>();

  useEffect(() => {
    if (values.FinancialInstitutionNumber) {
      const FinancialInstitutionID = getFinancialInstitutionIDByNumber(values.FinancialInstitutionNumber);

      if (FinancialInstitutionID) {
        fetchFinancialInstitutionBranches({ FinancialInstitutionID: Number(FinancialInstitutionID) });
      }
    }
  }, [values.FinancialInstitutionNumber]);

  useEffect(() => {
    setCurrentValues({
      ...values,
    });
  }, [values]);

  useEffect(() => {
    void validateForm();
  }, [paymentUpdateRows]);

  return null;
}

const BankingUpdateMiniDialogue: FC = ({
  isVisible,
  type,
  close,
  onSave,
  defaultValues,
  financialInstitutions,
  updateType,
  originalValue,
}: BankingUpdateDialogueProps) => {
  const {
    SubTitle,
    Box,
    ElementContainer,
    TextField,
    Select,
    GoogleAddressField,
    Radio,
    DatepickerSimple,
    Checkbox,
    Icon,
  } = vp;

  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.AccountingSystem.OpenTab.BankingUpdateMiniDialogue',
  });

  const { t: tRoot } = useTranslation();

  const { t: tElinx } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.OpenTab.Elinx' });
  const [currentValues, setCurrentValues] = useState(defaultValues);
  const [showElinxConnect, setShowElinxConnect] = useState(false);
  const { account: response } = useAccountingSoftware();
  const { Country } = response;
  const iQ11Enabled = isIQ11Enabled(response);
  const { userPermissions: permissions } = useAccountingSoftware();
  const canUseElinx = hasElinxPermission(type, permissions, Boolean(response.PayLinkEnabled));
  const elinxRows = useElinxRows({ type, country: Country, iQ11Enabled });
  const { LockedSelectOptions } = useAccountingSoftware().account;
  const elinxFormType: 'Pay' | 'Collect' = type === 'Receivable' ? 'Collect' : 'Pay';

  const elinxSeed = {
    ...mergeElinxDefaults(defaultValues, originalValue?.Customer),
    ElinxPaymentSelectionMethod:
      defaultValues.ElinxPaymentSelectionMethod ||
      getDefaultPaymentSelectionMethods(
        elinxFormType,
        toElinxCountry(Country),
        LockedSelectOptions,
        undefined,
        'single',
      ),
    ElinxBankingPaymentRail:
      defaultValues.ElinxBankingPaymentRail ||
      getDefaultBankingPaymentRails(elinxFormType, toElinxCountry(Country), LockedSelectOptions, undefined, 'single'),
  };

  const [financialInstitutionBranchesResponse, fetchFinancialInstitutionBranches] =
    useFetchFinancialInstitutionBranches({});

  // Card tokens are scoped to whichever client account the embed url was
  // generated for, so an asp row's card must be tokenized against the
  // contact's client account — not the logged-in one.
  const aspClientAccountID = originalValue ? getAspRowExtras(originalValue)?.AspClientAccountID : undefined;

  const [elinxConnectSettings, setElinxConnectSettings] = useState<vp.EmbedUrlRequest>({
    PaymentSelectionMethod: 'credit',
    ClientControlled: false,
    RedirectMethod: 'JavascriptMessage',
    ClientAccountID: aspClientAccountID || undefined,
    RedirectURL: '',
  });

  const paymentUpdateRows = buildPaymentUpdateRows({
    type,
    updateType,
    country: Country,
    branchTransitNumbers: Object.values(financialInstitutionBranchesResponse?.Branches || {}).map((value) => {
      const { BranchTransitNumber, Address, TownCity, PostalCode, Province } = value;

      return {
        label: `${BranchTransitNumber} ${Address}, ${TownCity}, ${PostalCode}, ${Province}`,
        value: BranchTransitNumber.toString(),
        renderOption: (
          <div>
            {' '}
            {BranchTransitNumber} <br /> {`${Address}, ${TownCity}, ${PostalCode}, ${Province}`}
          </div>
        ),
      };
    }),
    financialInstitutions: Object.values(financialInstitutions || {}).map((value) => ({
      label: `${value.FinancialInstitutionNumber} ${value.FIName}`,
      value: value.FinancialInstitutionNumber,
      renderOption: (
        <div>
          {value.FinancialInstitutionNumber} <br /> {value.FIName}
        </div>
      ),
    })),
    originalValue,
    t: tRoot,
    tElinx,
    elinxRows,
    canUseElinx,
  });

  const paymentMethodOptions = Object.values(
    getPaymentMethodsNeededDropdown(
      originalValue || { hasBankAccount: false, hasCreditCard: false, hasDebitCard: false, hasInterac: false },
      Country,
      type,
    ),
  );

  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  const onSuccess = (eventValues: PaymentUpdate) => {
    setShowSaveSuccess(true);

    setTimeout(() => {
      setShowSaveSuccess(false);
      onSave(eventValues);
    }, 2000);
  };

  return (
    <Formik<PaymentUpdate>
      initialValues={
        updateType === 'Edit'
          ? { ...defaultValues, ...elinxSeed }
          : {
              FinancialInstitutionNumber: '',
              BankTransitNumber: '',
              AccountNumber: '',
              RoutingNumber: '',
              Email: '',
              Phone: '',
              Question: '',
              Answer: '',
              InteracMethod: '',
              PaymentMethod: paymentMethodOptions.length === 1 ? paymentMethodOptions[0] : '',
              TokenDebit: '',
              TokenCredit: '',
              PhoneNumber: '',
              City: '',
              Address: '',
              State: '',
              ZipCode: '',
              IdentificationNumber: '',
              PaymentDate: defaultValues.PaymentDate,
              ...elinxSeed,
            }
      }
      onSubmit={onSuccess}
      enableReinitialize
      validationSchema={Yup.object().shape(getValidationSchema(currentValues, paymentUpdateRows))}
      validateOnChange
      validateOnBlur
    >
      {({
        values,
        touched,
        errors,
        isValid,
        handleChange,
        handleBlur,
        handleSubmit,
        setValues,
        setTouched,
        setFieldValue,
        resetForm,
      }) => {
        return (
          <>
            <div className={[styles.dialogueMini, styles.dialogueSide, isVisible ? styles.show : ''].join(' ')}>
              <div className={styles.dialogueContainer}>
                <form className={[styles.dialogueBlock].join(' ')} onSubmit={handleSubmit}>
                  <BankingUpdateMiniFormEffects
                    setCurrentValues={setCurrentValues}
                    paymentUpdateRows={paymentUpdateRows}
                    fetchFinancialInstitutionBranches={fetchFinancialInstitutionBranches}
                    getFinancialInstitutionIDByNumber={getFinancialInstitutionIDByNumber}
                  />

                  <div className={styles.dialogueSideHeader}>
                    <Box jc="center">
                      <SubTitle
                        subTitle={
                          updateType === 'Add'
                            ? t('title_Add', { defaultValue: 'Add Payment Method' })
                            : t('title_Edit', { defaultValue: 'Edit Payment Method' })
                        }
                        fontSize="20px"
                      />
                    </Box>
                  </div>

                  <div className={styles.dialogueSideContainer}>
                    <div className={styles.dialogueSideContent}>
                      {paymentUpdateRows
                        .filter((element) => element.visible(values))
                        .map((element) =>
                          getFormElement(
                            element,
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            setValues,
                            setTouched,
                            setFieldValue,
                          ),
                        )}

                      {isElinxActive(values) && <ElinxInfoBanner type={type} />}

                      {isElinxActive(values) && (
                        <div className={styles.dialogueTerm}>
                          {type === 'Receivable'
                            ? t('accountInfoSaved_Receivable', {
                                defaultValue: 'Account information is saved once the collection is sent.',
                              })
                            : t('accountInfoSaved_Payable', {
                                defaultValue: 'Account information is saved once the payment is sent.',
                              })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.dialogueSideFooter}>
                    {showSaveSuccess && (
                      <div className={styles.PaymentMethodSuccess}>
                        <div className={styles.PaymentMethodSuccessIcon}>
                          <Icon icon="check" height="10px" width="10px" color="primary-light" />
                        </div>{' '}
                        {updateType === 'Add'
                          ? t('paymentMethodAdded', { defaultValue: 'Payment Method Added' })
                          : t('paymentMethodUpdated', { defaultValue: 'Payment Method Updated' })}
                      </div>
                    )}

                    {!showSaveSuccess && (
                      <Box jc="center" gap="sm" flx="row" ai="center">
                        <Button
                          id="confirmationDialogue-close"
                          colorScheme="secondary"
                          style={{ width: '100%' }}
                          onClick={() => {
                            resetForm();
                            close();
                          }}
                        >
                          {t('cancel', { defaultValue: 'Cancel' })}
                        </Button>

                        {values.PaymentMethod !== 'Credit Card' && values.PaymentMethod !== 'Debit Card' && (
                          <Button
                            id="save-and-collect"
                            type="submit"
                            colorScheme="primary"
                            disabled={!isValid}
                            style={{ width: '100%' }}
                          >
                            {t('save', { defaultValue: 'Save' })}
                          </Button>
                        )}

                        {values.PaymentMethod === 'Credit Card' && (
                          <Button
                            id="save-and-collect"
                            type="button"
                            colorScheme="primary"
                            style={{ width: '100%' }}
                            onClick={() => {
                              setElinxConnectSettings({
                                RedirectURL: 'https://vopay.com',
                                Country,
                                PaymentSelectionMethod: 'credit',
                                ClientControlled: false,
                                RedirectMethod: 'JavascriptMessage',
                                ClientAccountID: aspClientAccountID || undefined,
                              });

                              setShowElinxConnect(true);
                            }}
                          >
                            {t('provide', { defaultValue: 'Provide' })}
                          </Button>
                        )}

                        {values.PaymentMethod === 'Debit Card' && (
                          <Button
                            id="save-and-collect"
                            type="button"
                            colorScheme="primary"
                            style={{ width: '100%' }}
                            onClick={() => {
                              setElinxConnectSettings({
                                RedirectURL: 'https://vopay.com',
                                Country,
                                PaymentSelectionMethod: 'debitcard',
                                ClientControlled: false,
                                RedirectMethod: 'JavascriptMessage',
                                ClientAccountID: aspClientAccountID || undefined,
                              });

                              setShowElinxConnect(true);
                            }}
                          >
                            {t('provide', { defaultValue: 'Provide' })}
                          </Button>
                        )}
                      </Box>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {showElinxConnect && (
              <ELinxConnect
                setting={elinxConnectSettings}
                onSuccess={(eventValues) => {
                  const tokenized =
                    values.PaymentMethod === 'Debit Card'
                      ? { ...values, TokenDebit: eventValues.data.Token }
                      : { ...values, TokenCredit: eventValues.data.Token };

                  // Submit the tokenized values directly rather than via
                  // submitForm(): setValues only lands on the next render, so a
                  // submitForm() here would run against values that still have
                  // no token. setValues keeps the form itself in sync.
                  void setValues(tokenized);
                  onSuccess(tokenized);
                  setShowElinxConnect(false);
                }}
                onClose={() => {
                  setShowElinxConnect(false);
                }}
              />
            )}
          </>
        );
      }}
    </Formik>
  );

  function getFormElement(
    element: PaymentUpdateRows,
    values: PaymentUpdate,
    errors: FormikErrors<PaymentUpdate>,
    touched: FormikTouched<PaymentUpdate>,
    handleChange: (event: React.ChangeEvent<any>) => void,
    handleBlur: (event: React.FocusEvent<any>) => void,
    setValues: (values: React.SetStateAction<PaymentUpdate>, shouldValidate?: boolean | undefined) => void,
    setTouched: (touched: FormikTouched<PaymentUpdate>, shouldValidate?: boolean | undefined) => void,
    setFieldValue: (field: string, value: string | boolean) => void,
  ) {
    const {
      type: elementType,
      id,
      preset,
      placeholder,
      label,
      showValue,
      require: required,
      getSelectOptions,
      getRadioOptions,
      container,
      paymentRows,
      isDisabled,
      renderOption,
      filterable,
      subtitle,
      disabled,
    } = element;

    if (element.type === 'elinx') {
      return element.render
        ? element.render({ values, errors, touched, handleChange, handleBlur, setFieldValue })
        : null;
    }

    if (container === 'wrapper' || id === undefined) {
      return (
        <Box gap="sm" ai="fs" width="100%">
          {paymentRows
            .filter((subElement) => subElement.visible(values))
            .map((subElement) =>
              getFormElement(
                subElement,
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setValues,
                setTouched,
                setFieldValue,
              ),
            )}
        </Box>
      );
    }

    const isRequired = required(values);

    switch (elementType) {
      case 'checkbox':
        return (
          <div className={styles.elinxToggle}>
            <Checkbox
              name={id}
              value={!values[id]}
              disabled={disabled ? disabled(values) : false}
              onBlur={() => {}}
              onChange={() => setFieldValue(id, !values[id])}
              size="sm"
              variant="rounded"
              hasNoPadding
            />

            <div className={styles.elinxToggleText}>
              <span className={styles.elinxToggleLabel}>{label}</span>
              {subtitle && <span className={styles.elinxToggleSubtitle}>{subtitle}</span>}
            </div>
          </div>
        );
      case 'input':
        return (
          <ElementContainer
            width="100%"
            padding="0 0 6px"
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            isBold={false}
            input={
              <TextField
                variant="outlineShadow"
                preset={preset}
                name={id}
                placeholder={placeholder}
                onBlur={handleBlur}
                onChange={handleChange}
                width="100%"
                disabled={isDisabled}
                value={String(values[id] || '')}
                error={errors[id] && touched[id] ? errors[id]?.toString() : null}
              />
            }
          />
        );
      case 'select':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            padding="0 0 6px"
            justifyContent="flex-start"
            isBold={false}
            input={
              <Select
                version="2"
                renderOption={renderOption}
                options={getSelectOptions ? getSelectOptions(values) : []}
                placeholder={placeholder || ''}
                name={id}
                onBlur={handleBlur}
                onChange={handleChange}
                renderSelectedValue={(value, { Placeholder }) => (
                  <div className={styles.dropDownValue}>
                    {showValue
                      ? value?.value || <Placeholder />
                      : value?.renderOption || value?.label || <Placeholder />}
                  </div>
                )}
                filterable={filterable}
                value={String(values[id] || '')}
                sort={false}
                errorMsg={errors[id] && touched[id] ? errors[id]?.toString() : undefined}
              />
            }
          />
        );
      case 'googleAddress':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            padding="0 0 6px"
            justifyContent="flex-start"
            isBold={false}
            input={
              <GoogleAddressField
                variant="outlineShadow"
                ISOCountry={['US']}
                preset={preset}
                placeholder=""
                name={id}
                onBlur={handleBlur}
                onChange={handleChange}
                value={String(values[id] || '')}
                error={errors[id] && touched[id] ? errors[id]?.toString() : null}
                onGoogleAddressSelect={(addressComponents: vp.AddressComponents) => {
                  handleChange(vp.createSyntheticEvent('Address', addressComponents.address));
                  handleChange(vp.createSyntheticEvent('City', addressComponents.city));
                  handleChange(vp.createSyntheticEvent('State', addressComponents.province));
                  handleChange(vp.createSyntheticEvent('ZipCode', addressComponents.postalcode));
                }}
              />
            }
          />
        );
      case 'radio':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            padding="0 0 6px"
            isBold={false}
            justifyContent="flex-start"
            input={
              <Radio
                key={id}
                name={id}
                value={String(values[id])}
                options={getRadioOptions ? getRadioOptions(values) : {}}
                noOrdering
                onBlur={() => {}}
                onChange={handleChange}
                variant="inline"
                setCheckedValue={(value) => {
                  setValues({ ...values, Language: value as 'en' | 'fr' });
                }}
              />
            }
          />
        );

      case 'selectFinanacialInstitution':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            padding="0 0 6px"
            isBold={false}
            justifyContent="flex-start"
            input={
              <Select
                version="2"
                options={getSelectOptions ? getSelectOptions(values) : []}
                placeholder={placeholder || ''}
                name={id}
                renderSelectedValue={(value, { Placeholder }) => (
                  <div className={styles.dropDownValue}>
                    {showValue
                      ? value?.value || <Placeholder />
                      : value?.renderOption || value?.label || <Placeholder />}
                  </div>
                )}
                filterable={filterable}
                renderOption={renderOption}
                onBlur={handleBlur}
                onChange={(e) => {
                  setValues({ ...values, BankTransitNumber: '' });
                  setTouched({ ...touched, BankTransitNumber: false });
                  handleChange(e);
                }}
                value={String(values[id] || '')}
                sort={false}
                errorMsg={errors[id] && touched[id] ? errors[id]?.toString() : undefined}
              />
            }
          />
        );

      case 'date':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            padding="0 0 6px"
            isBold={false}
            justifyContent="flex-start"
            input={
              <DatepickerSimple
                variant="outlineShadow"
                name={id}
                value={String(values[id] || '')}
                onBlur={handleBlur}
                onChange={handleChange}
                error={errors[id] ? errors[id]?.toString() : null}
              />
            }
          />
        );
      default:
        throw new Error(`Invalid payment type: ${type}`);
    }
  }

  function getValidationSchema(
    ctx: PaymentUpdate,
    rows: PaymentUpdateRows[],
    initialValidationSchema?: { [key: string]: any },
  ): { [key: string]: any } {
    const validations = rows.reduce(
      (existingFields, row) =>
        row.id && row.container === 'field'
          ? {
              ...existingFields,
              [row.id]: row.validate ? row.validate(ctx) : undefined,
            }
          : { ...existingFields, ...getValidationSchema(ctx, row.paymentRows) },
      { ...initialValidationSchema },
    );

    return validations;
  }

  function getFinancialInstitutionIDByNumber(number: string) {
    for (const key in financialInstitutions) {
      if (financialInstitutions[key].FinancialInstitutionNumber.toString() === number) {
        return financialInstitutions[key].FinancialInstitutionID;
      }
    }

    return null; // Return null if the number is not found
  }
};

export default BankingUpdateMiniDialogue;
